import { createStructuredSelector } from "reselect";
import {
    makeSelectLiteTradeMarketDataBySymbol,
    makeSelectorGetLiteTradeAccountMetrics,
    makeSelectorGetLiteTradeAccountPositionInfo,
    makeSelectorGetLiteTradeAssetInfo
} from "../../../../modules/LiteTrade/liteTradeSelectors";
import { connect } from "react-redux";
import withDebounced from "../../../../hooks/withDebounced";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { LITE_TRADE_POSITION_SIDE } from "../../../../modules/LiteTrade/liteTradeConstants";
import { Loader } from "../../../../components/Loader/Loader";
import {
    MobilePositionUpdateBadgeStyled,
    MobilePositionUpdateBottomPlaceholderStyled,
    MobilePositionUpdateChartStyled,
    MobilePositionUpdateDividerStyled,
    MobilePositionUpdateFixedMenuStyled,
    MobilePositionUpdateInfoPanelStyled,
    MobilePositionUpdateOrderProtectionTitleStyled,
} from "./styled/MobilePositionUpdateStyled";
import classNames from "classnames";
import _ from "lodash";
import MobilePositionAmountEditor from "../MobilePositionAmountEditor/MobilePositionAmountEditor";
import {
    addMarketDataWatchAction,
    cancelLiteTradeOrderAction,
    closeLiteTradePositionAction,
    removeMarketDataWatchAction,
    updateLiteTradeOrderAction,
    updateLiteTradePositionAction
} from "../../../../modules/LiteTrade/liteTradeActions";
import { ORDER_TYPES } from "../../../../components/OrderTypeSelector/orderTypeSelectorConstants";
import { Button } from "react-bootstrap";
import MobilePositionParamEditor from "../MobilePositionParamEditor/MobilePositionParamEditor";
import MobilePositionParamSwitch from "../MobilePositionParamSwitch/MobilePositionParamSwitch";
import { useMobilePositionFormErrors } from "../mobilePositionHooks";
import appHistory from "../../../../app/appHistory";
import {
    makeSelectorMobilePositionUpdateInitialData,
    makeSelectorMobilePositionUpdateProtectionOrderInfo
} from "./mobilePositionUpdateSelectors";
import amountFormat from "../../../../utils/amountFormat";
import { BinIcon } from "../../../../components/icon/Icon";
import { useHideKeyboardOnScroll } from "../../../mobileAppHooks";
import LiteTradeChartTickerWrapper
    from "../../../../modules/LiteTrade/components/LiteTradeChart/LiteTradeChartTickerWrapper";
import { useAssetInstance } from "../../../../modules/Asset/assetHooks";
import { useLangContext } from "../../../../providers/LangProvider/LangProviderContext";

function MobilePositionUpdate(props) {
    const {
        addMarketDataWatch,
        assetInfo,
        cancelOrder,
        closePosition,
        marketDataSymbol,
        metrics,
        orderInfo,
        orderStopLoss,
        orderTakeProfit,
        removeMarketDataWatch,
        symbol,
        updateOrder,
        updatePosition,
    } = props;
    const {getLangPath} = useLangContext();
    const [lots, setLots] = useState('0');
    const [amount, setAmount] = useState('0');
    const [inited, setInited] = useState(false);
    const [additionalFields, setAdditionalFields] = useState({});
    const [stopLossActive, setStopLossActive] = useState(false);
    const [takeProfitActive, setTakeProfitActive] = useState(false);
    const isBuy = useMemo(() => (orderInfo?.side === LITE_TRADE_POSITION_SIDE.BUY), [orderInfo]);
    const {isAssetInstanceReady, assetInstance} = useAssetInstance(symbol);

    const getCurrentPriceSide = useCallback(() =>
        orderInfo.side === LITE_TRADE_POSITION_SIDE.BUY ? 'bid' : 'ask', [orderInfo]);

    const getCurrentPrice = useCallback(() => {
        let side = getCurrentPriceSide();
        return marketDataSymbol?.[side] ?? 0;
    }, [getCurrentPriceSide, marketDataSymbol]);

    const pl = useMemo(() => assetInstance?.getPositionPL({
        openPrice: orderInfo?.price,
        side: orderInfo?.side,
        quantity: orderInfo?.quantity,
    }, getCurrentPrice()), [assetInstance, getCurrentPrice, orderInfo]);
    const plDiff = useMemo(() => pl === null ? null : pl / (metrics?.balance + metrics?.credit) * 100, [metrics, pl]);

    useHideKeyboardOnScroll();

    const initialOrderValues = useMemo(() => ({
        lots: orderInfo.quantity / assetInstance?.asset.info.lotSize,
        price: orderInfo.price,
        stopLoss: orderStopLoss?.legs[0].price || null,
        takeProfit: orderTakeProfit?.legs[0].price || null,
    }), [orderInfo, assetInstance, orderStopLoss, orderTakeProfit]);

    const newOrderValues = useMemo(() => ({
        lots,
        price: orderInfo.type === ORDER_TYPES.STOP
            ? additionalFields['stopPrice']
            : orderInfo.type === ORDER_TYPES.LIMIT
                ? additionalFields['limitPrice']
                : orderInfo.price,
        stopLoss: stopLossActive ? additionalFields['stopLoss'] : null,
        takeProfit: takeProfitActive ? additionalFields['takeProfit'] : null,
    }), [additionalFields, orderInfo, lots, stopLossActive, takeProfitActive]);

    const isOrderParamsChanged = useMemo(() => !_.isEqual(initialOrderValues, newOrderValues), [initialOrderValues, newOrderValues])

    useEffect(() => {
        if (assetInfo) {
            addMarketDataWatch({fromComponentName: 'order_change', symbol: assetInfo.symbol});
        }
        return () => {
            removeMarketDataWatch({fromComponentName: 'order_change'});
        }
    }, [assetInfo, addMarketDataWatch, removeMarketDataWatch]);

    const amountChangeHandler = useCallback((value) => {
        setAmount(value);

        const lots = assetInstance.amountToLots(value, getCurrentPrice())
        setLots(assetInstance.roundLots(lots));
        localStorage.setItem('inputLastLots', assetInstance.roundLots(lots));
    }, [assetInstance, getCurrentPrice]);

    const lotsChangeHandler = useCallback((value) => {
        setLots(value);
        localStorage.setItem('inputLastLots', value);

        const amount = assetInstance.lotsToAmount(parseFloat(value) || 0, getCurrentPrice());
        setAmount(_.round(amount, 2).toString());
    }, [assetInstance, getCurrentPrice]);

    const additionalFieldChange = useCallback((field, value) => {
        setAdditionalFields((prev) => {
            return {
                ...prev,
                [field]: value,
            }
        });
    }, []);

    const getProtectionOrderDefaultPrice = useCallback(() =>
            orderInfo.type === ORDER_TYPES.MARKET
                ? getCurrentPrice()
                : orderInfo.type === ORDER_TYPES.STOP
                    ? additionalFields['stopPrice']
                    : additionalFields['limitPrice'],
        [additionalFields, getCurrentPrice, orderInfo]
    );

    const getProtectionOrderDefaultSpreadAmount = useCallback(() => {
        if (!marketDataSymbol) return;
        const spread = marketDataSymbol.ask - marketDataSymbol.bid;
        return 20 * spread;
    }, [marketDataSymbol]);

    const getDefaultStopLoss = useCallback(() => {
        const spreadAmount = getProtectionOrderDefaultSpreadAmount();
        if (!spreadAmount) return;
        const price = getProtectionOrderDefaultPrice();
        return isBuy ? (price - spreadAmount) : (price + spreadAmount);
    }, [getProtectionOrderDefaultPrice, getProtectionOrderDefaultSpreadAmount, isBuy]);

    const getDefaultTakeProfit = useCallback(() => {
        const spreadAmount = getProtectionOrderDefaultSpreadAmount();
        if (!spreadAmount) return;
        const price = getProtectionOrderDefaultPrice();
        return isBuy ? (price + spreadAmount) : (price - spreadAmount);
    }, [getProtectionOrderDefaultPrice, getProtectionOrderDefaultSpreadAmount, isBuy]);

    const stopLossActiveChangeHandler = useCallback(() => {
        setStopLossActive(prevState => !prevState);
        if (!additionalFields['stopLoss']) {
            additionalFieldChange('stopLoss', getDefaultStopLoss());
        }
    }, [additionalFieldChange, additionalFields, getDefaultStopLoss]);

    const takeProfitActiveChangeHandler = useCallback(() => {
        setTakeProfitActive(prevState => !prevState);
        if (!additionalFields['takeProfit']) {
            additionalFieldChange('takeProfit', getDefaultTakeProfit());
        }
    }, [additionalFieldChange, additionalFields, getDefaultTakeProfit]);

    const limitPriceChange = useCallback((value) => additionalFieldChange('limitPrice', value), [additionalFieldChange]);
    const stopPriceChange = useCallback((value) => additionalFieldChange('stopPrice', value), [additionalFieldChange]);
    const takeProfitChange = useCallback((value) => additionalFieldChange('takeProfit', value), [additionalFieldChange]);
    const stopLossChange = useCallback((value) => additionalFieldChange('stopLoss', value), [additionalFieldChange]);

    useEffect(() => {
        if (!inited && assetInstance && isAssetInstanceReady && marketDataSymbol) {
            lotsChangeHandler(orderInfo.quantity / assetInstance.asset.info.lotSize);
            setAdditionalFields(prev => ({
                ...prev,
                limitPrice: orderInfo.price,
                stopPrice: orderInfo.price,
                stopLoss: orderStopLoss?.legs[0].price,
                takeProfit: orderTakeProfit?.legs[0].price,
            }));
            if (orderStopLoss) setStopLossActive(true);
            if (orderTakeProfit) setTakeProfitActive(true);
            setInited(true);
        }
    }, [assetInstance, getCurrentPrice, inited, isAssetInstanceReady, isBuy, lotsChangeHandler, marketDataSymbol, orderInfo, orderStopLoss, orderTakeProfit, stopLossActiveChangeHandler, takeProfitActiveChangeHandler])

    const saveChangesClickHandler = useCallback(() => {
        const saveChangesParams = {
            initialOrderStopLoss: orderStopLoss,
            initialOrderTakeProfit: orderTakeProfit,
            initialQuantity: orderInfo.quantity,
            orderCode: orderInfo.orderCode,
            orderType: orderInfo.type,
            positionCode: orderInfo.positionCode,
            quantity: lots * assetInstance.getLotSize(),
            side: orderInfo.side,
            stopLossActive,
            symbol: orderInfo.symbol,
            takeProfitActive,
            ...additionalFields,
        };
        if (orderInfo.type === ORDER_TYPES.MARKET) {
            updatePosition(saveChangesParams);
        } else {
            updateOrder(saveChangesParams);
        }
    }, [
        additionalFields,
        assetInstance,
        lots,
        orderInfo,
        orderStopLoss,
        orderTakeProfit,
        stopLossActive,
        takeProfitActive,
        updateOrder,
        updatePosition,
    ]);

    const cancelOrderHandler = useCallback(() => {
        if (orderInfo.type === ORDER_TYPES.MARKET) {
            closePosition({
                positionCode: orderInfo.positionCode,
                quantity: orderInfo.quantity,
                side: orderInfo.side === LITE_TRADE_POSITION_SIDE.SELL
                    ? LITE_TRADE_POSITION_SIDE.BUY
                    : LITE_TRADE_POSITION_SIDE.SELL,
                symbol: orderInfo.symbol,
                onSuccessCallback: appHistory.goBack,
            });
        } else {
            cancelOrder({
                orderCode: orderInfo.orderCode,
                onSuccessCallback: appHistory.goBack,
            });
        }
    }, [cancelOrder, closePosition, orderInfo]);

    const formErrors = useMobilePositionFormErrors({
        additionalFields,
        amount,
        assetInstance,
        currentPrice: getCurrentPrice(),
        isCheckFreeMargin: orderInfo.type !== ORDER_TYPES.MARKET,
        lots,
        metrics,
        orderType: orderInfo?.type,
        side: orderInfo.side,
        stopLossActive,
        takeProfitActive,
    });

    const isFormErrorsEmpty = useMemo(() => Object.values(formErrors).length === 0, [formErrors]);

    const isDisabledSaveOrder = !isOrderParamsChanged || !isFormErrorsEmpty;

    const currentPriceKey = useMemo(() =>
        `lite_version.order.${getCurrentPriceSide() === 'bid' ? "bid_price" : "ask_price"}`, [getCurrentPriceSide]);
    const currentPriceLabel = getLangPath(currentPriceKey);
    const protectionOrderComparePriceLabel = useMemo(() => {
        const key = orderInfo.type === ORDER_TYPES.LIMIT
            ? 'lite_version.order.types.limit.price'
            : orderInfo.type === ORDER_TYPES.STOP
                ? 'lite_version.order.types.stop.price'
                : currentPriceKey;
        return getLangPath(key)
    }, [currentPriceKey, getLangPath, orderInfo]);

    if (!assetInfo || !orderInfo || !marketDataSymbol || !inited) {
        return <Loader show={true}/>
    }

    return <>
        <MobilePositionUpdateInfoPanelStyled className="mt-3">
            <div className="price">{getCurrentPrice()}</div>
            {
                orderInfo.type === ORDER_TYPES.MARKET &&
                <div className={classNames("d-flex align-items-end f16 fw600", {
                    'negative': pl < 0,
                    'positive': pl > 0,
                })}>{
                    pl !== null && (
                        <div className="pl">
                            <span className="ms-2">
                                {amountFormat(pl, 2, '$', true)}
                            </span>
                            <span className="ms-2">
                                <BinIcon
                                    style={{fontSize: '1.25em'}}
                                    name={plDiff > 0 ? 'IcGrowth' : 'IcReduction'}
                                />
                                {amountFormat(plDiff, 2)}%
                            </span>
                        </div>
                    )
                }
                </div>
            }
        </MobilePositionUpdateInfoPanelStyled>
        <div className="d-flex gap-2 mt-2">
            <MobilePositionUpdateBadgeStyled className={classNames({
                'positive': orderInfo.side === LITE_TRADE_POSITION_SIDE.BUY,
                'negative': orderInfo.side === LITE_TRADE_POSITION_SIDE.SELL,
            })}>
                {getLangPath(`lite_version.order.${orderInfo.side === LITE_TRADE_POSITION_SIDE.BUY ? 'buy' : 'sell'}`)}
            </MobilePositionUpdateBadgeStyled>
            <MobilePositionUpdateBadgeStyled>
                {getLangPath(`lite_version.order.types.${_.toLower(orderInfo.type)}.type`)}
            </MobilePositionUpdateBadgeStyled>
        </div>
        <MobilePositionAmountEditor
            amount={amount}
            amountChangeHandler={amountChangeHandler}
            assetInstance={assetInstance}
            className="mt-3"
            currentPrice={getCurrentPrice()}
            lots={lots}
            maxLots={orderInfo.type === ORDER_TYPES.MARKET
                ? initialOrderValues.lots
                : assetInstance.amountToLots(metrics['marginFree'], getCurrentPrice())
            }
            lotsChangeHandler={lotsChangeHandler}
            error={formErrors['lots']}
        />
        <MobilePositionUpdateDividerStyled/>
        {
            orderInfo.type === ORDER_TYPES.LIMIT && <>
                <MobilePositionParamEditor
                    label={getLangPath('lite_version.order.types.limit.price')}
                    inputPriceLabel={getLangPath('lite_version.order.types.limit.price')}
                    comparePriceLabel={currentPriceLabel}
                    min={0}
                    onChange={limitPriceChange}
                    priceIncrement={assetInfo.info.priceIncrement}
                    value={additionalFields['limitPrice']}
                    error={formErrors['limitPrice']}
                />
                <MobilePositionUpdateDividerStyled/>
            </>
        }
        {
            orderInfo.type === ORDER_TYPES.STOP && <>
                <MobilePositionParamEditor
                    label={getLangPath('lite_version.order.types.stop.price')}
                    inputPriceLabel={getLangPath('lite_version.order.types.stop.price')}
                    comparePriceLabel={currentPriceLabel}
                    min={0}
                    onChange={stopPriceChange}
                    priceIncrement={assetInfo.info.priceIncrement}
                    value={additionalFields['stopPrice']}
                    error={formErrors['stopPrice']}
                />
                <MobilePositionUpdateDividerStyled/>
            </>
        }

        <MobilePositionUpdateOrderProtectionTitleStyled>
            {getLangPath('lite_version.edit_position.order_protection')}
        </MobilePositionUpdateOrderProtectionTitleStyled>
        <MobilePositionParamSwitch
            label={getLangPath(`lite_version.order.stop_loss`)}
            checked={stopLossActive}
            onChange={stopLossActiveChangeHandler}
        />
        {
            !!stopLossActive && <>
                <MobilePositionParamEditor
                    priceIncrement={assetInfo.info.priceIncrement}
                    label={getLangPath(`lite_version.order.price`)}
                    inputPriceLabel={getLangPath('lite_version.order.stop_loss')}
                    comparePriceLabel={protectionOrderComparePriceLabel}
                    min={0}
                    value={additionalFields['stopLoss']}
                    onChange={stopLossChange}
                    error={formErrors['stopLoss']}
                />
                <MobilePositionUpdateDividerStyled/>
            </>
        }

        <MobilePositionParamSwitch
            label={getLangPath(`lite_version.order.take_profit`)}
            checked={takeProfitActive}
            onChange={takeProfitActiveChangeHandler}
        />
        {
            !!takeProfitActive && <>
                <MobilePositionParamEditor
                    priceIncrement={assetInfo.info.priceIncrement}
                    label={getLangPath(`lite_version.order.price`)}
                    inputPriceLabel={getLangPath('lite_version.order.take_profit')}
                    comparePriceLabel={protectionOrderComparePriceLabel}
                    min={0}
                    value={additionalFields['takeProfit']}
                    onChange={takeProfitChange}
                    error={formErrors['takeProfit']}
                />
                <MobilePositionUpdateDividerStyled/>
            </>
        }

        <MobilePositionUpdateChartStyled>
            <LiteTradeChartTickerWrapper
                decimal={-Math.log10(assetInstance.getPriceIncrement())}
                symbol={assetInfo.symbol}
                onlyOneChartLine={isBuy ? 'ask' : 'bid'}
            />
        </MobilePositionUpdateChartStyled>

        <MobilePositionUpdateBottomPlaceholderStyled/>
        <MobilePositionUpdateFixedMenuStyled>
            <div className="d-flex flex-column">
                <Button variant="primary" size="lg" onClick={saveChangesClickHandler} disabled={isDisabledSaveOrder}>
                    {getLangPath(`lite_version.edit_position.${
                        orderInfo.type === ORDER_TYPES.MARKET && initialOrderValues.lots !== newOrderValues.lots
                            ? "close_position_partially"
                            : "save_changes"
                    }`)}
                </Button>
                <Button size="lg" variant="outline-info" className="mt-3" onClick={cancelOrderHandler}>
                    {getLangPath(`lite_version.edit_position.${
                        orderInfo.type === ORDER_TYPES.MARKET ? "close_position" : "cancel_order"
                    }`)}
                </Button>
            </div>
        </MobilePositionUpdateFixedMenuStyled>
    </>
}

const mapStateToProps = createStructuredSelector({
    assetInfo: (state, props) => makeSelectorGetLiteTradeAssetInfo(props.symbol)(state, props),
    orderInfo: (state, props) => makeSelectorMobilePositionUpdateInitialData({
        orderId: props.orderId,
        positionCode: props.positionCode,
    })(state, props),
    marketDataSymbol: (state, props) => makeSelectLiteTradeMarketDataBySymbol(props.symbol)(state, props),
    metrics: (state, props) => makeSelectorGetLiteTradeAccountMetrics(props.accountCode)(state, props),
    orderStopLoss: (state, props) => makeSelectorMobilePositionUpdateProtectionOrderInfo({
        orderId: props.orderId,
        positionCode: props.positionCode,
        protectionOrderType: ORDER_TYPES.STOP,
    })(state, props),
    orderTakeProfit: (state, props) => makeSelectorMobilePositionUpdateProtectionOrderInfo({
        orderId: props.orderId,
        positionCode: props.positionCode,
        protectionOrderType: ORDER_TYPES.LIMIT,
    })(state, props),
    positionInfo: (state, props) => makeSelectorGetLiteTradeAccountPositionInfo(props.positionCode)(state, props),
});

const mapDispatchToProps = (dispatch) => ({
    addMarketDataWatch: payload => dispatch(addMarketDataWatchAction(payload)),
    cancelOrder: payload => dispatch(cancelLiteTradeOrderAction(payload)),
    closePosition: payload => dispatch(closeLiteTradePositionAction(payload)),
    updateOrder: payload => dispatch(updateLiteTradeOrderAction(payload)),
    updatePosition: payload => dispatch(updateLiteTradePositionAction(payload)),
    removeMarketDataWatch: payload => dispatch(removeMarketDataWatchAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withDebounced({
    debounce: 1000,
    propNames: ['marketDataSymbol', 'metrics'],
})(memo(MobilePositionUpdate)));
